// A physical connection's pipe in three dimensions: its route at 45° / 90° with
// its fall, its elbows, the snap of a dragged bend and its tally of fittings.
// DOM-free and connection-free (connections.ts resolves the ends and calls in),
// unit-tested.
import type { Vec2 } from '../shared/types';

export interface Vec3 { x: number; y: number; z: number }

/** mm: closer than this is the same place, the same height, the same direction. */
const TOL = 0.5;

const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const mul = (a: Vec3, k: number): Vec3 => ({ x: a.x * k, y: a.y * k, z: a.z * k });
const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const len = (a: Vec3): number => Math.hypot(a.x, a.y, a.z);
const norm = (a: Vec3): Vec3 => { const l = len(a) || 1; return mul(a, 1 / l); };
const planLen = (a: Vec3, b: Vec3): number => Math.hypot(b.x - a.x, b.y - a.y);

/** Whether a run in plan is square or at 45° (or has no length in plan). */
export function planAligned(dx: number, dy: number): boolean {
  const ax = Math.abs(dx), ay = Math.abs(dy);
  return ax < TOL || ay < TOL || Math.abs(ax - ay) < TOL;
}

/** Whether a run `dz` over `l` in plan is level, at the fall `slope` (a
 *  fraction, falling forward), at 45° up or down, or plumb. */
export function gradeAligned(dz: number, l: number, slope: number): boolean {
  return l < TOL || Math.abs(dz) < TOL || Math.abs(dz + slope * l) < TOL || Math.abs(Math.abs(dz) - l) < TOL;
}

/** The knee of an octilinear plan path from `p` to `q`: straight along the longer
 *  run, then at 45° — null when they already line up. */
function planKnee(p: Vec2, q: Vec2): Vec2 | null {
  const dx = q.x - p.x, dy = q.y - p.y;
  if (planAligned(dx, dy)) return null;
  const ax = Math.abs(dx), ay = Math.abs(dy);
  return ax >= ay
    ? { x: p.x + Math.sign(dx) * (ax - ay), y: p.y }
    : { x: p.x, y: p.y + Math.sign(dy) * (ay - ax) };
}

/** One leg of a pipe, `p` → `q` (`p` left out): straight when the two already
 *  line up at 45° / 90° in plan and level, at the fall, at 45° or plumb in
 *  height; else a knee in plan, the run at the fall (level when the two are
 *  level, the exact grade when it is the fall or 45°), and a riser at `q` for
 *  the height left. */
export function pipeLeg(p: Vec3, q: Vec3, slope: number): Vec3[] {
  const L = planLen(p, q), dz = q.z - p.z;
  if (L < TOL) return [{ ...q }];
  const knee = planKnee(p, q);
  if (!knee && gradeAligned(dz, L, slope)) return [{ ...q }];
  const plan: Vec2[] = knee ? [knee, q] : [q];
  let Lp = 0, prev: Vec2 = p;
  for (const v of plan) { Lp += Math.hypot(v.x - prev.x, v.y - prev.y); prev = v; }
  const g = Math.abs(dz) < TOL ? 0
    : Math.abs(dz / Lp + slope) * Lp < TOL || Math.abs(Math.abs(dz) - Lp) < TOL ? dz / Lp
    : -slope;
  const out: Vec3[] = [];
  let z = p.z;
  prev = p;
  for (const v of plan) {
    z += g * Math.hypot(v.x - prev.x, v.y - prev.y);
    out.push({ x: v.x, y: v.y, z });
    prev = v;
  }
  const last = out[out.length - 1];
  if (Math.abs(last.z - q.z) < TOL) last.z = q.z;
  else out.push({ ...q });
  return out;
}

/** A pipe's route through its control points, each leg by `pipeLeg`; `leg[s]` is
 *  the leg (control point `leg[s]` → the next) segment `s` belongs to. Runs that
 *  carry straight on through a point are one run. */
export function pipePath(ctrl: readonly Vec3[], slope: number): { pts: Vec3[]; leg: number[] } {
  const pts: Vec3[] = [{ ...ctrl[0] }];
  const leg: number[] = [];
  for (let i = 0; i < ctrl.length - 1; i++) {
    for (const v of pipeLeg(ctrl[i], ctrl[i + 1], slope)) {
      if (len(sub(v, pts[pts.length - 1])) < 1e-6) continue;
      pts.push(v);
      leg.push(i);
    }
  }
  for (let k = pts.length - 2; k >= 1; k--) {
    const u = sub(pts[k], pts[k - 1]), w = sub(pts[k + 1], pts[k]);
    const c = dot(u, w) / ((len(u) * len(w)) || 1);
    if (c > 1 - 1e-9) { pts.splice(k, 1); leg.splice(k, 1); }
  }
  return { pts, leg };
}

/** Samples per leg of a hose. */
const HOSE_STEPS = 24;

/** A flexible hose's route through its control points: one smooth curve
 *  (Hermite, Catmull-Rom through its bends), no elbows. It hangs: at an end
 *  it runs plumb, down from the higher end and up into the lower one, and
 *  straight in plan when it has no bends; level ends join straight. `leg` as
 *  `pipePath`'s. */
export function hosePath(ctrl: readonly Vec3[]): { pts: Vec3[]; leg: number[] } {
  const n = ctrl.length;
  if (n < 2) return { pts: ctrl.map((p) => ({ ...p })), leg: [] };
  const plumb = (a: Vec3, b: Vec3): Vec3 => {
    const d = sub(b, a), l = len(d);
    return Math.abs(d.z) < TOL ? d : { x: 0, y: 0, z: Math.sign(d.z) * l };
  };
  const tan = ctrl.map((p, i) => (i === 0 ? plumb(p, ctrl[1]) : i === n - 1 ? plumb(ctrl[n - 2], p) : mul(sub(ctrl[i + 1], ctrl[i - 1]), 0.5)));
  const pts: Vec3[] = [{ ...ctrl[0] }];
  const leg: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const a = ctrl[i], b = ctrl[i + 1], ma = tan[i], mb = tan[i + 1];
    for (let k = 1; k <= HOSE_STEPS; k++) {
      const t = k / HOSE_STEPS, t2 = t * t, t3 = t2 * t;
      const h00 = 2 * t3 - 3 * t2 + 1, h10 = t3 - 2 * t2 + t, h01 = -2 * t3 + 3 * t2, h11 = t3 - t2;
      const p = k === HOSE_STEPS ? { ...b } : add(add(mul(a, h00), mul(ma, h10)), add(mul(b, h01), mul(mb, h11)));
      if (len(sub(p, pts[pts.length - 1])) < 1e-6) continue;
      pts.push(p);
      leg.push(i);
    }
  }
  return { pts, leg };
}

/** A hose drawn: its centreline as it runs, no elbows and no straight pieces,
 *  its length along the curve. */
export function hoseGeometry(pts: readonly Vec3[]): PipeGeom {
  let length = 0;
  for (let i = 1; i < pts.length; i++) length += len(sub(pts[i], pts[i - 1]));
  return { centre: pts.map((p) => ({ ...p })), bends: [], straights: [], length };
}

/** The route seen from above: points stacked by a riser are one, its leg the
 *  first's. */
export function planOfPath(path: { pts: Vec3[]; leg: number[] }): { pts: Vec2[]; leg: number[] } {
  const pts: Vec2[] = [{ x: path.pts[0].x, y: path.pts[0].y }];
  const leg: number[] = [];
  for (let s = 1; s < path.pts.length; s++) {
    const v = path.pts[s], last = pts[pts.length - 1];
    if (Math.hypot(v.x - last.x, v.y - last.y) < 1e-6) continue;
    pts.push({ x: v.x, y: v.y });
    leg.push(path.leg[s - 1]);
  }
  if (pts.length === 1) { pts.push({ ...pts[0] }); leg.push(0); }
  return { pts, leg };
}

/** One elbow: where it turns (`at`), by how much (deg, the deflection), the
 *  radius it is drawn at (the type's, less where its runs are too short), where
 *  it leaves the runs (`t0`, `t1`) and its arc. */
export interface PipeBend { at: Vec3; angle: number; radius: number; t0: Vec3; t1: Vec3; arc: Vec3[] }

/** A pipe drawn with its elbows: the centreline (straights and arcs sampled), the
 *  elbows, the straight pieces between them and the whole length. */
export interface PipeGeom { centre: Vec3[]; bends: PipeBend[]; straights: Array<[Vec3, Vec3]>; length: number }

/** The elbows of a route at centreline radius `radius`. A run shorter than its
 *  two elbows need is shared half and half (an end run whole), the radius
 *  shrinking to fit. */
export function pipeGeometry(pts: readonly Vec3[], radius: number): PipeGeom {
  const n = pts.length;
  const segLen = pts.slice(1).map((p, i) => len(sub(p, pts[i])));
  const bends: Array<PipeBend | null> = [];
  for (let i = 1; i < n - 1; i++) {
    const u = norm(sub(pts[i], pts[i - 1])), w = norm(sub(pts[i + 1], pts[i]));
    const th = Math.acos(Math.max(-1, Math.min(1, dot(u, w))));
    if (th < 1e-3 || th > Math.PI - 1e-3) { bends.push(null); continue; }
    const half = Math.tan(th / 2);
    const before = i - 1 === 0 ? segLen[i - 1] : segLen[i - 1] / 2;
    const after = i + 1 === n - 1 ? segLen[i] : segLen[i] / 2;
    const T = Math.min(Math.max(0, radius) * half, before, after);
    const R = T / half;
    const t0 = sub(pts[i], mul(u, T)), t1 = add(pts[i], mul(w, T));
    const nn = norm(sub(w, mul(u, dot(u, w))));
    const centre = add(t0, mul(nn, R));
    const steps = Math.max(2, Math.ceil(th / (Math.PI / 16)));
    const arc: Vec3[] = [];
    for (let k = 0; k <= steps; k++) {
      const f = (th * k) / steps;
      arc.push(add(centre, add(mul(nn, -R * Math.cos(f)), mul(u, R * Math.sin(f)))));
    }
    bends.push({ at: { ...pts[i] }, angle: (th * 180) / Math.PI, radius: R, t0, t1, arc });
  }
  const centre: Vec3[] = [{ ...pts[0] }];
  const straights: Array<[Vec3, Vec3]> = [];
  let length = 0;
  let from = pts[0];
  const straightTo = (to: Vec3): void => {
    const l = len(sub(to, from));
    if (l > 1e-6) { straights.push([from, to]); length += l; centre.push({ ...to }); }
  };
  for (let i = 1; i < n - 1; i++) {
    const b = bends[i - 1];
    if (!b) { straightTo(pts[i]); from = pts[i]; continue; }
    straightTo(b.t0);
    for (let k = 1; k < b.arc.length; k++) centre.push(b.arc[k]);
    length += b.radius * (b.angle * Math.PI) / 180;
    from = b.t1;
  }
  if (n > 1) straightTo(pts[n - 1]);
  return { centre, bends: bends.filter((b): b is PipeBend => !!b), straights, length };
}

/** What a pipe is made of, counted: its 90° and 45° elbows (within 6°, so a
 *  run at its fall still meets a riser at 90°), its other bends (their
 *  angles), its straight pieces, their length and the whole length (mm). */
export interface PipeTally { bends90: number; bends45: number; other: number[]; straights: number; straightLength: number; length: number }

export function pipeTally(g: PipeGeom): PipeTally {
  const t: PipeTally = { bends90: 0, bends45: 0, other: [], straights: 0, straightLength: 0, length: g.length };
  for (const b of g.bends) {
    if (Math.abs(b.angle - 90) <= 6) t.bends90++;
    else if (Math.abs(b.angle - 45) <= 6) t.bends45++;
    else t.other.push(Math.round(b.angle));
  }
  for (const [a, b] of g.straights) {
    const l = len(sub(b, a));
    if (l < 1) continue;
    t.straights++;
    t.straightLength += l;
  }
  return t;
}

/** Where a dragged bend may go: on the level plane it stands on (a drag in plan
 *  or across in 3D), up and down its plumb line (a drag in height), or in a
 *  section's vertical plane through it along `u`. */
export type PipeMotion =
  | { kind: 'plane'; z: number }
  | { kind: 'vline'; x: number; y: number }
  | { kind: 'hline'; o: Vec3; u: Vec2 }
  | { kind: 'vplane'; o: Vec3; u: Vec2 };

/** The directions a run may leave a bend in: the eight in plan, each level, at
 *  the fall (`fall` mm per mm, signed for the way the run is walked) and at 45°
 *  up and down; and plumb up and down. */
function runDirections(fall: number): Vec3[] {
  const out: Vec3[] = [{ x: 0, y: 0, z: 1 }, { x: 0, y: 0, z: -1 }];
  for (let k = 0; k < 8; k++) {
    const a = (k * Math.PI) / 4;
    const c = Math.cos(a), s = Math.sin(a);
    for (const g of new Set([0, fall, 1, -1])) out.push(norm({ x: c, y: s, z: g }));
  }
  return out;
}

/**
 * Snap a bend being dragged to `raw` (on its motion) so the runs to its
 * neighbours leave square, at 45°, level, at the fall or plumb: a place that
 * lines up with BOTH neighbours within `tol` mm wins, else the nearest that lines
 * up with one, else `raw`. `slope` is the fall (a fraction) toward the next
 * neighbour (the To end).
 */
export function snapPipeVertex(raw: Vec3, prev: Vec3 | null, next: Vec3 | null, slope: number, motion: PipeMotion, tol: number): { p: Vec3; lines: number } {
  type Line = { o: Vec3; d: Vec3; who: number };
  const lines: Line[] = [];
  const points: Array<{ p: Vec3; who: number }> = [];
  const ray = (o: Vec3, d: Vec3, who: number): void => {
    if (motion.kind === 'vline' || motion.kind === 'hline') {
      // Where the ray meets the line the bend moves on, if it does (a ray along
      // that line lines up anywhere on it: no snap).
      const lo = motion.kind === 'vline' ? { x: motion.x, y: motion.y, z: 0 } : motion.o;
      const m = motion.kind === 'vline' ? { x: 0, y: 0, z: 1 } : norm({ x: motion.u.x, y: motion.u.y, z: 0 });
      const q = closestOfLines({ o, d }, { o: lo, d: m }, true);
      if (q) points.push({ p: q, who });
      return;
    }
    const pn: Vec3 = motion.kind === 'plane' ? { x: 0, y: 0, z: 1 } : { x: -motion.u.y, y: motion.u.x, z: 0 };
    const po: Vec3 = motion.kind === 'plane' ? { x: o.x, y: o.y, z: motion.z } : motion.o;
    const dn = dot(d, pn), off = dot(sub(po, o), pn);
    if (Math.abs(dn) > 1e-9) {
      const t = off / dn;
      if (t > 1e-6) points.push({ p: add(o, mul(d, t)), who });
    } else if (Math.abs(off) < TOL) lines.push({ o, d, who });
  };
  if (prev) for (const d of runDirections(-slope)) ray(prev, d, 0);
  if (next) for (const d of runDirections(slope)) ray(next, d, 1);
  const cands: Array<{ p: Vec3; n: number }> = points.map((q) => ({ p: q.p, n: 1 }));
  for (const l of lines) {
    const t = dot(sub(raw, l.o), l.d);
    if (t > 1e-6) cands.push({ p: add(l.o, mul(l.d, t)), n: 1 });
  }
  // A place on a line from each neighbour lines up with both.
  for (const a of lines) for (const b of lines) {
    if (a.who !== 0 || b.who !== 1) continue;
    const q = closestOfLines(a, b);
    if (q) cands.push({ p: q, n: 2 });
  }
  for (const a of lines) for (const q of points) {
    if (a.who === q.who) continue;
    const t = dot(sub(q.p, a.o), a.d);
    if (t > 1e-6 && len(sub(add(a.o, mul(a.d, t)), q.p)) < TOL) cands.push({ p: q.p, n: 2 });
  }
  let best: { p: Vec3; n: number; d: number } | null = null;
  for (const c of cands) {
    const d = len(sub(c.p, raw));
    if (d > tol) continue;
    if (!best || c.n > best.n || (c.n === best.n && d < best.d)) best = { ...c, d };
  }
  return best ? { p: best.p, lines: best.n } : { p: { ...raw }, lines: 0 };
}

/** Where two lines (forward of their origins) meet, within TOL; else null.
 *  `bothWays`: the second line runs both ways from its origin. The point is the
 *  second line's. */
function closestOfLines(a: { o: Vec3; d: Vec3 }, b: { o: Vec3; d: Vec3 }, bothWays = false): Vec3 | null {
  const r = sub(a.o, b.o);
  const A = dot(a.d, a.d), B = dot(a.d, b.d), C = dot(b.d, b.d), D = dot(a.d, r), E = dot(b.d, r);
  const den = A * C - B * B;
  if (Math.abs(den) < 1e-9) return null;
  const s = (B * E - C * D) / den, t = (A * E - B * D) / den;
  if (s <= 1e-6 || (!bothWays && t <= 1e-6)) return null;
  const p = add(a.o, mul(a.d, s)), q = add(b.o, mul(b.d, t));
  return len(sub(p, q)) < TOL ? q : null;
}

/** The height on the route where the plan point `p` falls on segment `s` (the
 *  nearest point of it) — for a bend added there. */
export function heightOnPath(pts: readonly Vec3[], p: Vec2): number {
  let best = { d: Infinity, z: pts[0]?.z ?? 0 };
  for (let s = 0; s < pts.length - 1; s++) {
    const a = pts[s], b = pts[s + 1];
    const dx = b.x - a.x, dy = b.y - a.y, L2 = dx * dx + dy * dy;
    const t = L2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L2)) : 0;
    const d = Math.hypot(a.x + dx * t - p.x, a.y + dy * t - p.y);
    if (d < best.d) best = { d, z: a.z + (b.z - a.z) * t };
  }
  return best.z;
}

/** A pipe seen from above, to scale: its centreline (elbows sampled, a riser a
 *  point), its two sides at the outer radius `r`, and where it rises or drops
 *  plumb (drawn as a circle). */
export interface PipePlan { centre: Vec2[]; sides: [Vec2[], Vec2[]]; risers: Vec2[]; r: number }

export function pipePlan(g: PipeGeom, outer: number): PipePlan {
  const centre: Vec2[] = [];
  for (const p of g.centre) {
    const last = centre[centre.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 0.01) centre.push({ x: p.x, y: p.y });
  }
  const risers: Vec2[] = [];
  for (const [a, b] of g.straights) {
    if (planLen(a, b) < TOL && Math.abs(b.z - a.z) > TOL) risers.push({ x: a.x, y: a.y });
  }
  const r = outer / 2;
  return { centre, sides: [offsetLine(centre, r), offsetLine(centre, -r)], risers, r };
}

/** The point of a route nearest `q` as a section view shows it (`t` along the
 *  view's line from `a` on `u`, `h` up; only what stands at or beyond the line,
 *  along `n`), within `tol`: its segment and its point in 3D. Null when none. */
export function pathPointInView(pts: readonly Vec3[], a: Vec2, u: Vec2, n: Vec2, q: { t: number; h: number }, tol: number): { seg: number; p: Vec3 } | null {
  const view = (p: Vec3): { t: number; h: number; d: number } => ({ t: (p.x - a.x) * u.x + (p.y - a.y) * u.y, h: p.z, d: (p.x - a.x) * n.x + (p.y - a.y) * n.y });
  let best: { seg: number; p: Vec3; dist: number } | null = null;
  for (let i = 0; i + 1 < pts.length; i++) {
    const A = view(pts[i]), B = view(pts[i + 1]);
    const dt = B.t - A.t, dh = B.h - A.h, L2 = dt * dt + dh * dh;
    const k = L2 > 1e-9 ? Math.max(0, Math.min(1, ((q.t - A.t) * dt + (q.h - A.h) * dh) / L2)) : 0;
    if (A.d + (B.d - A.d) * k < -TOL) continue;
    const dist = Math.hypot(A.t + dt * k - q.t, A.h + dh * k - q.h);
    if (dist > tol || (best && dist >= best.dist)) continue;
    const P = pts[i], Q = pts[i + 1];
    best = { seg: i, p: { x: P.x + (Q.x - P.x) * k, y: P.y + (Q.y - P.y) * k, z: P.z + (Q.z - P.z) * k }, dist };
  }
  return best ? { seg: best.seg, p: best.p } : null;
}

/** `pts` offset `d` to its left, mitred (a mitre at most 4 × `d` long). */
function offsetLine(pts: readonly Vec2[], d: number): Vec2[] {
  const n = pts.length;
  if (n < 2) return pts.map((p) => ({ ...p }));
  const ns: Vec2[] = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = pts[i + 1].x - pts[i].x, dy = pts[i + 1].y - pts[i].y, L = Math.hypot(dx, dy) || 1;
    ns.push({ x: -dy / L, y: dx / L });
  }
  return pts.map((p, i) => {
    const a = ns[Math.max(0, i - 1)], b = ns[Math.min(n - 2, i)];
    const mx = a.x + b.x, my = a.y + b.y, ml = Math.hypot(mx, my);
    if (ml < 1e-6) return { x: p.x + b.x * d, y: p.y + b.y * d };
    const ux = mx / ml, uy = my / ml;
    const k = Math.max(-4 * Math.abs(d), Math.min(4 * Math.abs(d), d / Math.max(1e-6, ux * b.x + uy * b.y)));
    return { x: p.x + ux * k, y: p.y + uy * k };
  });
}
